// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { APIService } from "../../utils/apiService";
import { AppConfig } from "../../config/config";
import axios, { HttpStatusCode } from "axios";
import { enqueueSnackbarMessage } from "@slices/commonSlice/common";
import { SnackMessage } from "@config/constant";


const initialState: BankAccountState = {
  state: "idle",
  stateMessage: null,
  errorMessage: null,
  banks: null,
};

interface BankAccountState {
  state: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  errorMessage: string | null;
  banks: Bank[] | null;
}

interface BankInterface {
  count: number;
  banks: Bank[];
}

export interface Bank {
  bankCode: string;
  swiftCode: string;
  bankName: string;
  bankLocation: string;
}

export const createBank = createAsyncThunk(
  "bankAccount/createBank",
  async (payload: Bank, { dispatch }) => {
    return new Promise<{ requestResponse: Bank }>(
      (resolve, reject) => {
        APIService.getInstance()
          .post(AppConfig.serviceUrls.banks, payload)
          .then((resp) => {
            if (resp.status === 201) {
              dispatch(
                enqueueSnackbarMessage({
                  message:
                    "Successfully Created the Bank.",
                  type: "success",
                })
              );
              resolve({ requestResponse: resp.data });
            } else {
              reject(resp.data.message);
            }
          })
          .catch((resp) => {
            const errorMessage =
              resp.response?.data?.message ?? "An unexpected error occurred";
            dispatch(
              enqueueSnackbarMessage({
                message: errorMessage,
                type: "error",
              })
            );
            reject(errorMessage);
          });
      }
    );
  }
);

export const getBankData = createAsyncThunk(
  "bankAccount/getBankData",
  async (_, { dispatch, rejectWithValue }) => {
    APIService.getCancelToken().cancel();
    const newCancelTokenSource = APIService.updateCancelToken();
    return new Promise<{ banks: BankInterface }>((resolve, reject) => {
      APIService.getInstance()
        .get(AppConfig.serviceUrls.banks, {cancelToken: newCancelTokenSource.token})
        .then((resp) => {
          if (resp.status === 200) {
            resolve({ banks: resp.data });
          } else {
            reject("Something went wrong");
          }
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            return rejectWithValue("Request canceled");
          }
          dispatch(
            enqueueSnackbarMessage({
              message:
                error.response?.status === HttpStatusCode.InternalServerError
                  ? SnackMessage.error.fetchBanksMessage
                  : String(error.response.data.message),
              type: "error",
            })
          );
          reject(error.response.data.message);
        });
    });
  }
);

export const BankSlice = createSlice({
  name: "getBankData",
  initialState,
  reducers: {
    updateStateMessage: (state, action: PayloadAction<string>) => {
      state.stateMessage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBankData.pending, (state, action) => {
        state.state = "loading";
        state.stateMessage = "Loading Bank Data";
      })
      .addCase(getBankData.fulfilled, (state, action) => {
        state.banks = action.payload.banks.banks;
        state.state = "success";
        state.stateMessage = null;
      })
      .addCase(getBankData.rejected, (state, payload) => {
        state.state = "failed";
        state.stateMessage = null;
      })
      .addCase(createBank.pending, (state) => {
        state.state = "loading";
        state.stateMessage = "Loading Bank Data";
      })
      .addCase(createBank.fulfilled, (state, action) => {
        if(state.banks){
          state.banks.push(action.payload.requestResponse);
        }
        state.state = "success";
        state.stateMessage = null;
      })
      .addCase(createBank.rejected, (state) => {
        state.state = "failed";
        state.stateMessage = null;
      });
  },
});

export const { updateStateMessage } = BankSlice.actions;

export default BankSlice.reducer;
